// ─────────────────────────────────────────────────────────────────────────────
// Sample Cancellation — History tab (prototype v3/v13).
//
// "Every cancellation and restore, newest first — one row per event."  There is
// no event table: a cancellation is two columns on SampleParameter, and its
// reason lives only in the audit trail.  So History is DERIVED from the form's
// audit rows — each `Status` row is one event; the field-level rows around it
// (CancellationReason, Billable, PrepBatch) are folded into it.
// Pure, so it is unit-testable.
// ─────────────────────────────────────────────────────────────────────────────

/** One row of GET /api/v1/audit/trail (the fields used here). */
export interface AuditTrailRow {
  oid:           string;
  createdDate:   string | null;
  createdBy:     string | null;
  recordId:      string | null;      // AuditData.ID — "SampleID · Test"
  propertyName:  string | null;
  oldValue:      string | null;
  newValue:      string | null;
  comment:       string | null;      // the reason: "CODE · comment" on a cancel
  reasonForChange?: string | null;
}

export interface HistoryRow {
  key:      string;
  date:     string | null;
  action:   'Cancelled' | 'Restored';
  sampleId: string;
  test:     string;
  /** Cancelled: the stage it was cancelled from.  Restored: the stage it went back to. */
  stage:    string;
  reason:   string;                  // the reason CODE (blank on a restore)
  comment:  string;
  billable: string;
  /** Prep batch the test was taken out of, e.g. "PB26090801_075" — blank when none. */
  batch:    string;
  by:       string;
}

const CANCELLED = 'Cancelled';
const SEP = ' · ';

/** "CODE · comment" → [code, comment]; a string with no separator is all comment. */
function splitReason(text: string): [string, string] {
  const i = text.indexOf(SEP);
  return i < 0 ? ['', text] : [text.slice(0, i), text.slice(i + SEP.length)];
}

export function buildHistory(rows: AuditTrailRow[]): HistoryRow[] {
  // The rows of one event share the test (recordId), the moment and the user.
  const sameEvent = (a: AuditTrailRow, b: AuditTrailRow) =>
    a.recordId === b.recordId && a.createdBy === b.createdBy
    && (a.createdDate ?? '').slice(0, 16) === (b.createdDate ?? '').slice(0, 16);
  const sibling = (status: AuditTrailRow, property: string) =>
    rows.find((r) => r.propertyName === property && sameEvent(r, status));

  return rows
    .filter((r) => r.propertyName === 'Status'
      && (r.newValue === CANCELLED || r.oldValue === CANCELLED))
    .map((r): HistoryRow => {
      const cancelled = r.newValue === CANCELLED;
      const [sampleId, test] = splitReason(r.recordId ?? '');
      const text = r.comment ?? r.reasonForChange ?? '';
      const [code, comment] = cancelled ? splitReason(text) : ['', text];
      const batch = sibling(r, 'PrepBatch')?.oldValue ?? '';
      return {
        key: r.oid,
        date: r.createdDate,
        action: cancelled ? 'Cancelled' : 'Restored',
        // An id with no separator is just the sample id.
        sampleId: sampleId || test,
        test: sampleId ? test : '',
        stage: (cancelled ? r.oldValue : r.newValue) ?? '',
        reason: code || (cancelled ? sibling(r, 'CancellationReason')?.newValue ?? '' : ''),
        comment,
        billable: cancelled ? sibling(r, 'Billable')?.newValue ?? '' : '',
        batch,
        by: r.createdBy ?? '',
      };
    })
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

/** How many cancellations used each reason code (the Reason Codes "Used" column). */
export function usedCounts(history: HistoryRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const h of history) if (h.action === 'Cancelled' && h.reason) out[h.reason] = (out[h.reason] ?? 0) + 1;
  return out;
}
